import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type Dispatch,
    type SetStateAction,
} from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchDocStructure } from "@hsl/core/documents/services";
import type {
    DocsData,
    LitStructureAPIParams,
} from "@hsl/core/documents/types";
import {
    type AllShareClassMeta,
    type FundDataWithType,
} from "@hsl/fund-explorer/metaSchemas";
import { type DocObj } from "@hsl/fund-explorer/types";

import kidOrKiid, { keyDocType } from "../../lib/kidOrKiid";
import useExplorerStore from "../../store/store";
import { mergeDocs } from "./helpers/mergeDocs";

type DocumentContextType = {
    activeShareClassId?: number;
    selectionActive: boolean;
    selectedDocumentURLs: SelectedURL[];
    docsData: DocsContextData;
    hasFactsheet: boolean;
    hasMemC: boolean;
    hasESG: boolean;
    wrappedFund: boolean;
    groupOn?: string;
    key: number;
    setSelectedDocumentURLs: Dispatch<SetStateAction<SelectedURL[]>>;
    toggleSelectionActive: () => void;
    addMultipleDocs: (docArr: SelectedURL[], nameSuffix?: string) => void;
    removeMultipleDocs: (urlArr: SelectedURL[]) => void;
    selectAllFactSheets: (shareClasses: AllShareClassMeta[]) => void;
    selectAllKIIDs: (shareClasses: AllShareClassMeta[]) => void;
    hasUpdates?: (x: string | number) => boolean;
    markRead?: (x: string | number) => void;
};

interface Props {
    children: React.ReactNode;
    fund: FundDataWithType;
    params: LitStructureAPIParams;
    hasUpdates?: DocumentContextType["hasUpdates"];
    markRead?: DocumentContextType["markRead"];
}

type DocsContextData = {
    data: DocsData | undefined;
    error: any;
    isLoading: boolean;
};

type SelectedURL = {
    name: string;
    value: string;
};

export const DocumentContext = createContext<DocumentContextType | undefined>(
    undefined,
);

export const DocumentContextProvider = ({
    params,
    fund,
    children,
    hasUpdates,
    markRead,
}: Props) => {
    const [selectionActive, setSelectionActive] = useState(false);
    const [selectedDocumentURLs, setSelectedDocumentURLs] = useState<
        SelectedURL[]
    >([]);
    const {
        litStructureId: confId,
        jurisdictionId,
        productType,
        tabs,
    } = useExplorerStore((state) => state.config);
    const docsTab = tabs.find((t) => t.key === "documents");

    const { data, error, isLoading } = useQuery({
        queryKey: ["docs", params, confId],
        queryFn: () => fetchDocStructure(confId, params),
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchInterval: false,
    });

    const addMultipleDocs = useCallback(
        (docArr: SelectedURL[], nameSuffix?: string) => {
            setSelectedDocumentURLs((selectedDocumentURLs) => {
                const newDocs = [...selectedDocumentURLs];
                // if document urls match then we need to combine the file names into one
                docArr
                    .filter((x) => x.value !== "-")
                    .forEach((docObj) => {
                        const { value, name } = docObj;
                        const valueMatch = newDocs.findIndex(
                            (x) => x.value === docObj.value,
                        );
                        if (valueMatch === -1) {
                            newDocs.push({
                                name: `${name} ${nameSuffix ?? ""}`.trim(),
                                value,
                            });
                        } else if (
                            !newDocs[valueMatch]?.name!.includes(docObj.name)
                        ) {
                            newDocs[valueMatch] = {
                                name: `${docObj.name}, ${
                                    newDocs[valueMatch]!.name
                                }`.trim(),
                                value: newDocs[valueMatch]!.value,
                            };
                        }
                    });

                return newDocs;
            });
        },
        [],
    );

    const removeMultipleDocs = useCallback((docArr: SelectedURL[]) => {
        setSelectedDocumentURLs((selectedDocumentURLs) => {
            const newDocs = [...selectedDocumentURLs];
            docArr.forEach((docObj) => {
                const index = newDocs.findIndex(
                    (x) => x.value === docObj.value,
                );
                if (index > -1) {
                    newDocs.splice(index, 1);
                }
            });
            return newDocs;
        });
    }, []);

    const selectAll = useCallback(
        (type: "factSheet" | "kiid", shareClasses: AllShareClassMeta[]) => {
            const allDocs = shareClasses.flatMap((shareClass) => {
                const {
                    shortName,
                    factSheet,
                    memberCommunication,
                    iterationName,
                } = shareClass;

                // value could be kiid or kid depending on jurisdiction
                const { value: kiid } = kidOrKiid(
                    jurisdictionId,
                    shareClass,
                    productType,
                );

                const fsMcDocs = mergeDocs(factSheet, memberCommunication);

                function parseDoc(doc: string | DocObj[]) {
                    if (typeof doc === "string") {
                        return {
                            name: shortName ?? iterationName,
                            value: doc,
                        };
                    } else {
                        return doc.map((docObj) => ({
                            name: `${shortName ?? iterationName} (${
                                docObj.lang
                            })`,
                            value: docObj.url,
                        }));
                    }
                }

                switch (type) {
                    case "factSheet":
                        return parseDoc(fsMcDocs);
                    case "kiid":
                        return parseDoc(kiid);
                }
            });
            const someDocsSelected = allDocs.some((x) =>
                selectedDocumentURLs.some((y) => x.value === y.value),
            );
            someDocsSelected
                ? removeMultipleDocs(allDocs as SelectedURL[])
                : addMultipleDocs(
                      allDocs as SelectedURL[],
                      type === "factSheet"
                          ? "Fact Sheet"
                          : keyDocType(
                                jurisdictionId,
                                productType,
                            ).toUpperCase(),
                  );
        },
        [
            addMultipleDocs,
            jurisdictionId,
            productType,
            removeMultipleDocs,
            selectedDocumentURLs,
        ],
    );

    const [ctxKey, setCtxKey] = useState(Date.now());

    const markReadFn = useCallback(
        (id: string | number) => {
            markRead?.(id);
            setCtxKey(Date.now());
        },
        [markRead],
    );

    const contextValue = useMemo(
        () => ({
            selectionActive,
            selectedDocumentURLs,
            docsData: {
                error,
                isLoading,
                data,
            },
            activeShareClassId: params.activeShareClassId,
            setSelectedDocumentURLs,
            toggleSelectionActive: () => setSelectionActive((s) => !s),
            addMultipleDocs,
            removeMultipleDocs,
            selectAllFactSheets: (shareClasses: AllShareClassMeta[]) =>
                selectAll("factSheet", shareClasses),
            selectAllKIIDs: (shareClasses: AllShareClassMeta[]) =>
                selectAll("kiid", shareClasses),
            hasMemC:
                fund.type === "pmc"
                    ? fund.data.data.card_variant === "Normal PMC Fund card"
                    : false,
            hasFactsheet:
                fund.type === "pmc"
                    ? fund.data.data.card_variant !== "Fund Group Fund Card"
                    : true,
            hasESG:
                fund.type === "pmc"
                    ? fund.data.data.card_variant ===
                      "Fund Group Card - Target Date"
                    : false,
            wrappedFund:
                fund.type === "pmc"
                    ? [true, "true", "True"].includes(
                          fund.data.data.wrappedFund,
                      )
                    : false,
            groupOn: docsTab ? docsTab.groupOn : undefined,
            hasUpdates,
            markRead: markReadFn,
            key: ctxKey,
        }),
        [
            selectionActive,
            selectedDocumentURLs,
            setSelectedDocumentURLs,
            data,
            error,
            isLoading,
            removeMultipleDocs,
            addMultipleDocs,
            selectAll,
            params.activeShareClassId,
            fund,
            docsTab,
            hasUpdates,
            markReadFn,
            ctxKey,
        ],
    );

    return (
        <DocumentContext.Provider value={contextValue}>
            {children}
        </DocumentContext.Provider>
    );
};

export function useDocumentContext() {
    const documentContext = useContext(DocumentContext);
    if (!documentContext) throw new Error("No DocumentContextProvider");
    return documentContext;
}
